import { FieldArray, Form, Formik, FormikHelpers } from "formik";
import * as Yup from "yup";
import Layout from "../../components/Layout";
import {
  Box,
  Button,
  capitalize,
  Chip,
  DialogActions,
  DialogContent,
  Grid2,
  InputAdornment,
  Pagination,
  Paper,
  TablePagination,
  Typography,
} from "@mui/material";
import React, { useCallback, useState } from "react";
import { useNavigate, useParams } from "react-router";
import SubmitButton from "../../components/SubmitButton";
import { useTranslation } from "react-i18next";
import AbladestelleFieldsContent from "../../components/abladestellen/AbladestelleFieldsContent";
import {
  useArchiviereAbladestelle,
  useCreateMaterial,
  useDeleteMaterial,
  useRestoreAbladestelle,
  useUpdateAbladestelle,
  useUpdateMaterial,
} from "../../client/hooks";
import DeleteIcon from "@mui/icons-material/Delete";
import TextField from "@mui/material/TextField";
import DeleteAbladestelleButton from "../../components/abladestellen/AbladestellenDeleteButton";
import { Abladestelle } from "../../api";
import ErrorAlert from "../../components/ErrorAlert";
import { notPermitted } from "../../errors";
import Guard from "../../components/Guard";
import { Archive, Search, Unarchive } from "@mui/icons-material";
import MaterialRow from "./MaterialRow";
import MaterialDialog from "./MaterialDialog";
import ConfirmNavigationDialog from "../../components/ConfirmNavigationDialog";
import { useConfirmationPageChangeDialog } from "../../hooks/useConfirmationPageChangeDialog";
import { useConfirmationNavDialog } from "../../hooks/useConfirmationNavDialog";
import AbladestellenFormDatenDirtyChecker from "../../components/abladestellen/AbladestellenFormDatenDirtyChecker";
import { enqueueSnackbar } from "notistack";
import { useMutation, useQueryClient } from "react-query";
import {
  useAbladestelle,
  useFetchMaterialsByAbladestelleId,
} from "../../client/queries";

export interface Material {
  id?: string;
  name: string;
  archiviert: boolean;
  abfallart: any;
  abfallartKey?: string;
  abladestelleId?: string;
  kostenProTonne?: string;
  kostenProKubikmeter?: string;
}

interface MaterialUpdated {
  id?: string;
  name: string;
  archiviert: boolean;
  abfallartId: any;
  abfallartKey?: string;
  abladestelleId?: string;
  kostenProTonne?: number;
  kostenProKubikmeter?: number;
}

interface Abfallart {
  id: string;
  name: string;
}

export interface ExtendedAbladestelleDaten {
  kurzname: string;
  firma: string;
  zusatz: string;
  ort: string;
  isDeponie: boolean;
  materials: Material[];
}

const validationSchema = Yup.object().shape({
  kurzname: Yup.string().required("Kurzname ist erforderlich"),
  firma: Yup.string(),
  ort: Yup.string(),
  isDeponie: Yup.boolean(),
  materials: Yup.array()
    .of(
      Yup.object().shape({
        name: Yup.string().required("Name ist erforderlich"),
        archiviert: Yup.boolean(),
        abfallart: Yup.object().required("Abfallart ist erforderlich"),
        kostenProTonne: Yup.string(),
        kostenProKubikmeter: Yup.string(),
      })
    )
    .min(0, ""),
});

export default function EditAbladestelle() {
  const { id: abladestelleId = "" } = useParams<{ id: string }>();
  const { t } = useTranslation("abladestelle");
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const updateAbladestelle = useUpdateAbladestelle();
  const createMaterial = useCreateMaterial();
  const deleteMaterial = useDeleteMaterial();
  const updateMaterial = useUpdateMaterial();
  const [search, setSearch] = useState<string>("");
  const [page, setPage] = useState<number>(0);
  const [limit, setLimit] = useState<number>(25);
  const [openDialogIndex, setOpenDialogIndex] = useState(null);
  const archiviereAbladestelle = useArchiveAbladestelle(abladestelleId);
  const unArchiviereAbladestelle = useUnArchiveAbladestelle(abladestelleId);
  const {
    showConfirmPageChangeDialog,
    handleConfirmPageChange,
    handleCancelPageChange,
    setShowConfirmPageChangeDialog,
    awaitUserDecision,
  } = useConfirmationPageChangeDialog();
  const { showConfirmDialog, handleConfirmBack, handleCancelBack } =
    useConfirmationNavDialog({ backRoute: "/sabladestellen" });

  const [isFormDirty, setIsFormDirty] = useState(false);

  const abladestelle = useAbladestelle(abladestelleId);

  const materialsData = useFetchMaterialsByAbladestelleId(
    abladestelleId,
    { search, page, limit },
    { enabled: !!abladestelleId, keepPreviousData: true }
  );
  const materials: MaterialUpdated[] = (materialsData?.items ??
    []) as MaterialUpdated[];
  const totalMaterials: number = materialsData?.totalElements ?? 0;

  const formatNumber = (value: number | undefined) => {
    if (value == null || isNaN(value)) return "";
    return value.toFixed(2).replace(".", ",");
  };

  const handleSearchChange = (value: string) => {
    setSearch(value);
    setPage(0);
  };

  const onSubmit = useCallback(
    async (
      values: ExtendedAbladestelleDaten,
      formikHelpers: FormikHelpers<ExtendedAbladestelleDaten>
    ) => {
      const { setSubmitting, setStatus, resetForm } = formikHelpers;
      try {
        // Update Abladestelle first
        await updateAbladestelle(abladestelleId, {
          kurzname: values.kurzname,
          firma: values.firma,
          zusatz: values.zusatz,
          ort: values.ort,
          isDeponie: values.isDeponie,
        });

        // Create or update each material
        const materialPromises = values.materials.map(async (material) => {
          let kostenProTonne;
          let kostenProKubikmeter;
          if (material.kostenProTonne !== undefined) {
            kostenProTonne = parseFloat(
              material.kostenProTonne.toString().replace(",", ".")
            );
          }
          if (material.kostenProKubikmeter !== undefined) {
            kostenProKubikmeter = parseFloat(
              material.kostenProKubikmeter.toString().replace(",", ".")
            );
          }
          const abfallartId = (material.abfallart as Abfallart)?.id ?? null;
          const materialData = {
            name: material.name,
            abladestelleId: abladestelleId,
            abfallartId: abfallartId,
            archiviert: material.archiviert,
            kostenProTonne: kostenProTonne,
            kostenProKubikmeter: kostenProKubikmeter,
          };
          if (material.id) {
            return updateMaterial(material.id, materialData);
          } else {
            const createdMaterial = await createMaterial(materialData);
            material.id = createdMaterial.id;
          }
        });

        await Promise.all(materialPromises);

        enqueueSnackbar("Änderungen gespeichert", {
          variant: "success",
        });

        setStatus(undefined);
        await queryClient.invalidateQueries(["abladestellen", abladestelleId]);
        await queryClient.invalidateQueries([
          "materialien",
          "abladestelle",
          abladestelleId,
        ]);
        resetForm({ values });
      } catch (error: any) {
        setStatus(error.message);
      } finally {
        setSubmitting(false);
      }
    },
    [
      updateAbladestelle,
      createMaterial,
      updateMaterial,
      abladestelleId,
      queryClient,
    ]
  );

  if (!abladestelle) {
    return <div>Loading...</div>;
  }

  const handleDialogOpen = (index: any) => {
    setOpenDialogIndex(index);
  };

  const handleDialogClose = () => {
    setOpenDialogIndex(null);
  };
  const handlePageChange = async (newPage: number) => {
    if (isFormDirty) {
      setShowConfirmPageChangeDialog(true);
      const userDescision = await awaitUserDecision();
      if (userDescision === "confirm") {
        setPage(newPage);
        setShowConfirmPageChangeDialog(false);
      } else {
        setShowConfirmPageChangeDialog(false);
      }
    } else {
      setPage(newPage);
    }
  };

  return (
    <Layout
      title={t("details-landfill-and-unloading-point")}
      onBackClick={() => navigate(-1)}
    >
      <Guard
        permission={"abladestelle:edit"}
        fallback={<ErrorAlert error={notPermitted()} />}
      >
        <Paper
          elevation={1}
          sx={{ marginTop: 2, padding: 3, marginLeft: 2, marginRight: 2 }}
        >
          <Guard permission={"abladestelle:delete"}>
            <DeleteAbladestelleButton
              id={abladestelleId}
            ></DeleteAbladestelleButton>
          </Guard>
          <ConfirmNavigationDialog
            open={showConfirmDialog}
            onConfirm={handleConfirmBack}
            onCancel={handleCancelBack}
            title={t("confirm-cancel-title")}
            message={t("confirm-cancel-message")}
          />
          <Formik<ExtendedAbladestelleDaten>
            initialValues={{
              kurzname: abladestelle.kurzname ?? "",
              firma: abladestelle.firma ?? "",
              zusatz: abladestelle.zusatz ?? "",
              ort: abladestelle.ort ?? "",
              isDeponie: abladestelle.isDeponie ?? false,
              materials: materials.map((material) => ({
                id: material.id,
                name: material.name,
                archiviert: material.archiviert ?? false,
                abfallart: material.abfallartId,
                abfallartKey: material.abfallartKey,
                kostenProTonne: formatNumber(material.kostenProTonne),
                kostenProKubikmeter: formatNumber(material.kostenProKubikmeter),
              })),
            }}
            enableReinitialize={true}
            validationSchema={validationSchema}
            onSubmit={onSubmit}
          >
            {({
              isSubmitting,
              isValidating,
              isValid,
              dirty,
              values,
              status,
            }) => {
              return (
                <>
                  <AbladestellenFormDatenDirtyChecker
                    setIsFormDatenDirty={setIsFormDirty}
                  />
                  <Typography variant="h5">
                    {abladestelle.geloeschtAm === undefined ? (
                      t("landfill-and-unloading-points")
                    ) : (
                      <>
                        <>Abladestelle</>
                        <Chip
                          size="small"
                          icon={<DeleteIcon />}
                          label={"gelöscht"}
                          color="error"
                        />
                      </>
                    )}
                    {abladestelle.archiviert !== undefined ? (
                      <Chip
                        size="small"
                        icon={<Archive />}
                        label={"archiviert"}
                        color="warning"
                      />
                    ) : (
                      <></>
                    )}
                  </Typography>
                  {status && (
                    <Typography color="error" variant="body1">
                      {status}
                    </Typography>
                  )}
                  <DialogContent>
                    <Form id="details-abladestelle">
                      <AbladestelleFieldsContent />
                      <Grid2 container spacing={2} sx={{ marginTop: 1 }}>
                        <Grid2 size={{ xs: 3 }}>
                          <TextField
                            sx={{ width: "100%" }}
                            type="search"
                            size={"medium"}
                            placeholder={capitalize(t("search"))}
                            value={search ?? ""}
                            onChange={(e) => handleSearchChange(e.target.value)}
                            slotProps={{
                              input: {
                                startAdornment: (
                                  <InputAdornment position="start">
                                    <Search />
                                  </InputAdornment>
                                ),
                              },
                            }}
                          />
                        </Grid2>
                      </Grid2>

                      <FieldArray name="materials">
                        {({ push, remove }) => (
                          <>
                            {values.materials.map(
                              (material: Material, index) => {
                                if (material) {
                                  return (
                                    <Box
                                      key={material.id || index}
                                      sx={{ marginTop: 2 }}
                                    >
                                      <MaterialDialog
                                        open={openDialogIndex === index}
                                        onClose={handleDialogClose}
                                        material={material}
                                        materialId={material.id}
                                        abladestelleId={abladestelleId}
                                      />

                                      <MaterialRow
                                        material={material}
                                        index={index}
                                        onDelete={() => {
                                          const materialId = material.id;
                                          if (materialId) {
                                            deleteMaterial(materialId).then(
                                              () => {
                                                remove(index);
                                              }
                                            );
                                          } else {
                                            remove(index);
                                          }
                                        }}
                                        onDialogOpen={() =>
                                          material.id && handleDialogOpen(index)
                                        }
                                      />
                                    </Box>
                                  );
                                } else {
                                  return null;
                                }
                              }
                            )}
                            <ConfirmNavigationDialog
                              open={showConfirmPageChangeDialog}
                              onConfirm={handleConfirmPageChange}
                              onCancel={handleCancelPageChange}
                              title={t("confirm-cancel-title")}
                              message={t("confirm-cancel-message")}
                            />
                            <TablePagination
                              component="div"
                              count={totalMaterials}
                              page={page}
                              onPageChange={async (_, newPage) => {
                                await handlePageChange(newPage);
                              }}
                              rowsPerPage={limit}
                              onRowsPerPageChange={(event) => {
                                setLimit(parseInt(event.target.value, 10));
                              }}
                              rowsPerPageOptions={[10, 25, 100]}
                            />
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "flex-end", // Aligns to the right
                                alignItems: "center", // Optional, for spacing
                              }}
                            >
                              <Pagination
                                page={page + 1}
                                count={Math.ceil(totalMaterials / limit)}
                                onChange={async (_, page) => {
                                  await handlePageChange(page - 1);
                                }}
                              />
                            </Box>

                            <Box sx={{ marginTop: 2 }}>
                              <Button
                                variant="contained"
                                color="secondary"
                                onClick={() =>
                                  push({
                                    name: "",
                                    archiviert: false,
                                    abfallart: "",
                                  })
                                }
                              >
                                {t("Material hinzufügen")}
                              </Button>
                            </Box>
                          </>
                        )}
                      </FieldArray>
                    </Form>
                  </DialogContent>
                  <DialogActions>
                    {abladestelle.archiviert === undefined ? (
                      <Button
                        startIcon={<Archive />}
                        variant="contained"
                        color="secondary"
                        onClick={() => {
                          archiviereAbladestelle.mutate();
                        }}
                      >
                        Archivieren
                      </Button>
                    ) : (
                      <Button
                        startIcon={<Unarchive />}
                        variant="contained"
                        color="success"
                        onClick={() => {
                          unArchiviereAbladestelle.mutate();
                        }}
                      >
                        Wiederherstellen
                      </Button>
                    )}
                    <SubmitButton
                      form="details-abladestelle"
                      type="submit"
                      variant="contained"
                      color="secondary"
                      loading={isSubmitting}
                      disabled={isValidating || !isValid || !dirty}
                    >
                      {capitalize(t("save"))}
                    </SubmitButton>
                  </DialogActions>
                </>
              );
            }}
          </Formik>
        </Paper>
      </Guard>
    </Layout>
  );
}

function useArchiveAbladestelle(id: string) {
  const archiveAbladestelle = useArchiviereAbladestelle();
  const queryClient = useQueryClient();

  return useMutation(
    () => {
      return archiveAbladestelle(id);
    },
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries(["abladestellen", id]);
      },
    }
  );
}

function useUnArchiveAbladestelle(id: string) {
  const unarchiveAbladestelle = useRestoreAbladestelle();
  const queryClient = useQueryClient();

  return useMutation(
    () => {
      return unarchiveAbladestelle(id);
    },
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries(["abladestellen", id]);
      },
    }
  );
}
